import { TrackingEvent } from "../../constants/events";
import { pushToDataLayer } from "../../providers/google/utils";
import { chunkItemsForGa } from "./utils/chunkUtils";
import { buildFlightRefundEcommerce } from "./utils/refundPayloadBuilder";

/**
 * Boş string'i null'a çevirir. DataLayer'a boş string gönderilmez.
 */
function sanitizeValue(value) {
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  return value;
}

/**
 * Payload objesindeki tüm değerleri sanitize eder (boş string -> null)
 */
function sanitizePayload(obj) {
  if (!obj || typeof obj !== "object") return obj;
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    result[key] = typeof value === "string" ? sanitizeValue(value) : value;
  }
  return result;
}

/**
 * Google Analytics - Uçak Provider
 * Uçak kategorisi için Google Analytics event'lerini yönetir
 */
export class FlightGoogleProvider {
  track(event, data) {
    switch (event) {
      case TrackingEvent.VIRTUAL_PV:
        this.trackVirtualPv(data);
        break;
      case TrackingEvent.SEARCH_RESULT:
        this.trackSearchResult(data);
        break;
      case TrackingEvent.VIEW_ITEM_LIST:
        this.trackViewItemList(data);
        break;
      case TrackingEvent.SELECT_ITEM:
        this.trackSelectItem(data);
        break;
      case TrackingEvent.VIEW_ITEM:
        this.trackViewItem(data);
        break;
      case TrackingEvent.ADD_TO_CART:
        this.trackAddToCart(data);
        break;
      case TrackingEvent.ADD_TO_WISHLIST:
        this.trackAddToWishlist(data);
        break;
      case TrackingEvent.BEGIN_CHECKOUT:
        this.trackBeginCheckout(data);
        break;
      case TrackingEvent.ADD_PAYMENT_INFO:
        this.trackAddPaymentInfo(data);
        break;
      case TrackingEvent.PURCHASE:
        this.trackPurchase(data);
        break;
      case TrackingEvent.REFUND:
        this.trackRefund(data);
        break;
      case TrackingEvent.FILTER_APPLIED:
        this.trackFilterApplied(data);
        break;
      case TrackingEvent.SORTING_APPLIED:
        this.trackSortingApplied(data);
        break;
      case TrackingEvent.PROMOTIONAL_BANNER_CLICKED:
        this.trackPromotionalBannerClicked(data);
        break;
      case TrackingEvent.SELECT_COUPON:
      case TrackingEvent.VIEW_COUPON:
      case TrackingEvent.COUPON_APPLIED:
        this.trackCouponEvent(event, data);
        break;
      default:
        this.trackGenericEvent(event, data);
    }
  }

  trackVirtualPv(data) {
    // Reservation sayfası için özel payload formatı (page_type: flightpassengerdetails)
    if (
      data.page_type === "flightpassengerdetails" ||
      data.page_type === "flightreservation"
    ) {
      pushToDataLayer(
        TrackingEvent.VIRTUAL_PV,
        sanitizePayload({
          event: data.event || "virtual_pv",
          environment: data.environment || "prod",
          virtual_page_url: data.virtual_page_url,
          page_type: data.page_type,
          flight_destination: data.flight_destination,
          flight_airlines: data.flight_airlines,
          language: data.language,
          trip_type: data.trip_type,
          origin_airport: data.origin_airport,
          destination_airport: data.destination_airport,
          num_adults: data.num_adults,
          num_children: data.num_children,
          num_infants: data.num_infants,
          travel_class: data.travel_class,
          departing_departure_date: data.departing_departure_date,
          returning_departure_date: data.returning_departure_date,
          departure_city: data.departure_city,
          arrival_city: data.arrival_city,
          location_type: data.location_type,
          turna_session_id: data.turna_session_id,
        }),
      );
      return;
    }

    // Payment sayfası için özel payload formatı (reservation ile aynı yapı)
    if (data.page_type === "flightpayment") {
      pushToDataLayer(
        TrackingEvent.VIRTUAL_PV,
        sanitizePayload({
          event: data.event || "virtual_pv",
          environment: data.environment || "prod",
          virtual_page_url: data.virtual_page_url,
          page_type: data.page_type,
          flight_destination: data.flight_destination,
          flight_airlines: data.flight_airlines,
          language: data.language,
          trip_type: data.trip_type,
          origin_airport: data.origin_airport,
          destination_airport: data.destination_airport,
          num_adults: data.num_adults,
          num_children: data.num_children,
          num_infants: data.num_infants,
          travel_class: data.travel_class,
          departing_departure_date: data.departing_departure_date,
          returning_departure_date: data.returning_departure_date,
          departure_city: data.departure_city,
          arrival_city: data.arrival_city,
          location_type: data.location_type,
          turna_session_id: data.turna_session_id,
        }),
      );
      return;
    }

    // Payment completed sayfası için özel payload formatı (payment ile aynı yapı)
    if (data.page_type === "flightpaymentcompleted") {
      pushToDataLayer(
        TrackingEvent.VIRTUAL_PV,
        sanitizePayload({
          event: data.event || "virtual_pv",
          environment: data.environment || "prod",
          virtual_page_url: data.virtual_page_url,
          page_type: data.page_type,
          flight_destination: data.flight_destination,
          flight_airlines: data.flight_airlines,
          language: data.language,
          trip_type: data.trip_type,
          origin_airport: data.origin_airport,
          destination_airport: data.destination_airport,
          num_adults: data.num_adults,
          num_children: data.num_children,
          num_infants: data.num_infants,
          travel_class: data.travel_class,
          departing_departure_date: data.departing_departure_date,
          returning_departure_date: data.returning_departure_date,
          departure_city: data.departure_city,
          arrival_city: data.arrival_city,
          location_type: data.location_type,
          turna_session_id: data.turna_session_id,
        }),
      );
      return;
    }

    // One-way, Round-trip ve Multi-city sayfaları için özel payload formatı
    if (
      data.page_type === "flightsearchlist" &&
      (data.trip_type === "OW" ||
        data.trip_type === "RT" ||
        data.trip_type === "MC")
    ) {
      pushToDataLayer(
        TrackingEvent.VIRTUAL_PV,
        sanitizePayload({
          event: data.event || "virtual_pv",
          environment: data.environment || "prod",
          virtual_page_url: data.virtual_page_url,
          page_type: data.page_type,
          flight_destination: data.flight_destination,
          flight_airlines: data.flight_airlines,
          language: data.language,
          trip_type: data.trip_type,
          origin_airport: data.origin_airport,
          destination_airport: data.destination_airport,
          num_adults: data.num_adults,
          num_children: data.num_children,
          num_infants: data.num_infants,
          travel_class: data.travel_class,
          departing_departure_date: data.departing_departure_date,
          returning_departure_date: data.returning_departure_date,
          departure_city: data.departure_city,
          arrival_city: data.arrival_city,
          location_type: data.location_type,
          turna_session_id: data.turna_session_id,
          ...(data.flight_legs_count != null && {
            flight_legs_count: data.flight_legs_count,
          }),
          ...(data.flight_routes != null && {
            flight_routes: data.flight_routes,
          }),
        }),
      );
    } else {
      // Diğer sayfalar için genel format
      pushToDataLayer(
        TrackingEvent.VIRTUAL_PV,
        sanitizePayload({
          environment: data.environment || "prod",
          virtual_page_url: data.virtual_page_url,
          page_type: data.page_type,
          flight_route: data.flight_route,
          flight_origin: data.flight_origin,
          flight_destination: data.flight_destination,
          flight_airlines: data.flight_airlines,
          trip_type: data.trip_type,
          cabin_class: data.cabin_class,
          passenger_count: data.passenger_count,
          language: data.language,
          turna_session_id: data.turna_session_id,
        }),
      );
    }
  }

  trackSearchResult(data) {
    pushToDataLayer(TrackingEvent.SEARCH_RESULT, {
      event: "custom_event",
      event_category: "search",
      event_action: "search_result",
      search_category: "flight",
      search_destination: data.search_destination,
      search_result_number: data.search_result_number,
    });
  }

  trackViewItemList(data) {
    pushToDataLayer({ ecommerce: null });

    const {
      trip_type,
      ow_id,
      rt_id,
      start_to_end_date,
      ecommerce,
      productType,
      ...restData
    } = data;

    const items = Array.isArray(ecommerce?.items) ? ecommerce.items : [];
    const itemListId = ecommerce?.item_list_id ?? "flight_search";
    const itemListName = ecommerce?.item_list_name || "Flight Search";

    const basePayload = {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "view_item_list",
      trip_type: trip_type,
      ow_id: ow_id !== undefined ? ow_id : null,
      rt_id: rt_id !== undefined ? rt_id : null,
      start_to_end_date:
        start_to_end_date !== undefined ? start_to_end_date : null,
      ...restData,
    };

    if (items.length === 0) return;

    const chunks = chunkItemsForGa(items, 8);

    chunks.forEach((chunk) => {
      if (!chunk || chunk.length === 0) return;

      pushToDataLayer(TrackingEvent.VIEW_ITEM_LIST, {
        ...basePayload,
        ecommerce: {
          item_list_id: itemListId,
          item_list_name: itemListName,
          items: chunk,
        },
      });
    });
  }

  trackSelectItem(data) {
    // Önce önceki ecommerce objesini temizle
    // Bu, GA4'ün önceki ecommerce verilerini kullanmaması için gereklidir
    pushToDataLayer({ ecommerce: null });

    // Root level alanları ayır (trip_type, ow_id, rt_id, start_to_end_date)
    const {
      trip_type,
      ow_id,
      rt_id,
      start_to_end_date,
      ecommerce,
      ...restData
    } = data;

    // Event'i gönder - ow_id/rt_id seferlerin origin-destination code'larına göre
    // null/undefined değerleri root seviyede gönderme (temiz payload)
    pushToDataLayer(TrackingEvent.SELECT_ITEM, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "select_item",
      trip_type: trip_type ?? null,
      ...(ow_id != null && ow_id !== "" && { ow_id }),
      ...(rt_id != null && rt_id !== "" && { rt_id }),
      ...(start_to_end_date != null &&
        start_to_end_date !== "" && { start_to_end_date }),
      // Ecommerce objesini ekle
      ecommerce: ecommerce,
      // Diğer alanları da ekle (gelecekte eklenebilecek alanlar için)
      ...restData,
    });
  }

  trackViewItem(data) {
    pushToDataLayer({ ecommerce: null });

    const { trip_type, rt_id, start_to_end_date, ecommerce, ...restData } =
      data;

    pushToDataLayer(TrackingEvent.VIEW_ITEM, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "view_item",
      trip_type: trip_type !== undefined ? trip_type : null,
      rt_id: rt_id !== undefined ? rt_id : null,
      start_to_end_date:
        start_to_end_date !== undefined ? start_to_end_date : null,
      ecommerce: ecommerce,
      ...restData,
    });
  }

  trackAddToCart(data) {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.ADD_TO_CART, {
      event: "ecom_event",
      event_category: data.event_category || "ecommerce",
      event_action: "add_to_cart",
      ...(data.trip_type != null && { trip_type: data.trip_type }),
      ...(data.ow_id !== undefined && { ow_id: data.ow_id }),
      ...(data.trip_type !== "MC" &&
        data.rt_id !== undefined &&
        data.rt_id !== null &&
        data.rt_id !== "" && { rt_id: data.rt_id }),
      ...(data.start_to_end_date && {
        start_to_end_date: data.start_to_end_date,
      }),
      ...(data.trip_type === "MC" && data.mc_route != null
        ? { mc_route: data.mc_route }
        : {}),
      ecommerce: data.ecommerce,
    });
  }

  trackAddToWishlist(data) {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.ADD_TO_WISHLIST, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "add_to_wishlist",
      ...(data.trip_type && { trip_type: data.trip_type }),
      ...(data.ow_id !== undefined && { ow_id: data.ow_id }),
      ...(data.rt_id !== undefined && { rt_id: data.rt_id }),
      ...(data.start_to_end_date && {
        start_to_end_date: data.start_to_end_date,
      }),
      ecommerce: data.ecommerce,
    });
  }

  trackBeginCheckout(data) {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.BEGIN_CHECKOUT, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: data.event_action || "begin_checkout", // Payload'dan event_action al, yoksa default
      ...(data.trip_type && { trip_type: data.trip_type }),
      ...(data.ow_id !== undefined && { ow_id: data.ow_id }),
      ...(data.trip_type !== "MC" &&
        data.rt_id !== undefined && { rt_id: data.rt_id }),
      ...(data.start_to_end_date && {
        start_to_end_date: data.start_to_end_date,
      }),
      ...(data.trip_type === "MC" && data.mc_route != null
        ? { mc_route: data.mc_route }
        : {}),
      ecommerce: data.ecommerce,
    });
  }

  trackAddPaymentInfo(data) {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.ADD_PAYMENT_INFO, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "add_payment_info",
      ecommerce: data.ecommerce,
    });
  }

  trackPurchase(data) {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.PURCHASE, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "purchase",
      ...(data.trip_type && { trip_type: data.trip_type }),
      ...(data.ow_id !== undefined && { ow_id: data.ow_id }),
      ...(data.trip_type !== "MC" &&
        data.rt_id !== undefined && { rt_id: data.rt_id }),
      ...(data.start_to_end_date && {
        start_to_end_date: data.start_to_end_date,
      }),
      ...(data.trip_type === "MC" && data.mc_route != null
        ? { mc_route: data.mc_route }
        : {}),
      ecommerce: data.ecommerce,
    });
  }

  trackRefund(data) {
    const ecommerce =
      data.ecommerce ??
      buildFlightRefundEcommerce({
        products: data.products,
        basketData: data.basketData,
        priceSummary: data.priceSummary,
        selectedSegmentType: data.selectedSegmentType,
        selectedPassengerIds: data.selectedPassengerIds ?? [],
        currency: data.currency,
      });

    if (!ecommerce) return;

    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.REFUND, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "refund",
      ecommerce,
    });
  }

  trackFilterApplied(data) {
    pushToDataLayer(TrackingEvent.FILTER_APPLIED, {
      event: "custom_event",
      event_category: "filtering",
      event_action: "filter_applied",
      filter_product_category: "flight",
      filter_category: data?.filterName,
      filter_subcategory: data?.label,
      sorting_type: `${data?.sort?.SortBy}_${data?.sort?.Ascending ? "asc" : "desc"}`,
    });
  }

  trackSortingApplied(data) {
    pushToDataLayer(TrackingEvent.SORTING_APPLIED, {
      event: "custom_event",
      event_category: "filtering",
      event_action: "sorting_applied",
      filter_product_category: "flight",
      filter_category: "sorting",
      filter_subcategory: `${data?.sortBy}_${data?.ascending ? "asc" : "desc"}`,
    });
  }

  trackPromotionalBannerClicked(data) {
    pushToDataLayer(TrackingEvent.PROMOTIONAL_BANNER_CLICKED, {
      event: "custom_event",
      event_category: "switch",
      event_action: `switch_to_${data?.type}`,
      [`${data?.type}_route`]: `${data?.from}-${data?.to}`,
      flight_route: `${data?.from}-${data?.to}`,
    });
  }

  trackCouponEvent(event, data) {
    const offer = data.offer || {};
    const currencyCode = data.currencyCode || "TRY";

    const couponName = offer?.code || "";
    const couponValue = offer?.discountText || "";

    pushToDataLayer(event, {
      event: "custom_event",
      event_category: "coupon",
      event_action: event,
      coupon_name: couponName,
      coupon_value: null,
      coupon_currency: currencyCode,
      coupon_category: "flight",
    });
  }

  trackGenericEvent(event, data) {
    pushToDataLayer(event, data);
  }
}
